// src/phase4/ui/BattleResultDialog.ts
// Battle Result Dialog - Shows battle statistics and allows return to maintenance

import Phaser from 'phaser';

export interface BattleResultData {
  outcome: 'VICTORY' | 'DEFEAT';
  enemiesKilled: number;
  wavesCompleted: number;
  baseHealthRemaining: number;
  moneyEarned: number;
}

export class BattleResultDialog extends Phaser.GameObjects.Container {
  private background!: Phaser.GameObjects.Graphics;
  private titleText!: Phaser.GameObjects.Text;
  private statsText!: Phaser.GameObjects.Text;
  private continueButton!: Phaser.GameObjects.Container;
  private onContinue?: () => void;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.setDepth(2000);
    this.setVisible(false);
    this.createDialog();
  }

  private createDialog(): void {
    const dialogWidth = 400;
    const dialogHeight = 320;

    // Semi-transparent overlay background (also acts as click blocker)
    const overlay = this.scene.add.graphics();
    overlay.fillStyle(0x000000, 0.6);
    overlay.fillRect(-400, -300, 800, 600);
    this.add(overlay);

    // Make overlay interactive to block clicks from reaching game elements below
    this.setSize(800, 600);
    this.setInteractive(new Phaser.Geom.Rectangle(-400, -300, 800, 600), Phaser.Geom.Rectangle.Contains);

    // Dialog background
    this.background = this.scene.add.graphics();
    this.background.fillStyle(0x201e1a, 0.95);
    this.background.fillRoundedRect(-dialogWidth / 2, -dialogHeight / 2, dialogWidth, dialogHeight, 16);
    this.background.lineStyle(3, 0xe6a640, 0.8);
    this.background.strokeRoundedRect(-dialogWidth / 2, -dialogHeight / 2, dialogWidth, dialogHeight, 16);
    this.add(this.background);

    // Title
    this.titleText = this.scene.add.text(0, -110, 'BATTLE COMPLETE', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add(this.titleText);

    // Stats text
    this.statsText = this.scene.add.text(0, -30, '', {
      fontSize: '16px',
      color: '#e8e4dc',
      fontFamily: 'monospace',
      align: 'center',
      lineSpacing: 8,
    }).setOrigin(0.5);
    this.add(this.statsText);

    // Continue button
    this.createButton(0, 100, 'Return to Maintenance');
  }

  private createButton(x: number, y: number, label: string): void {
    const buttonWidth = 220;
    const buttonHeight = 45;

    const buttonBg = this.scene.add.graphics();
    buttonBg.fillStyle(0xe6a640, 1);
    buttonBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
    buttonBg.lineStyle(2, 0xd4a030, 0.8);
    buttonBg.strokeRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);

    // Button text at (0, 0) relative to container center
    const buttonText = this.scene.add.text(0, 0, label, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Container at (x, y), children at relative positions
    this.continueButton = this.scene.add.container(x, y, [buttonBg, buttonText]);
    this.continueButton.setSize(buttonWidth, buttonHeight);

    // Button interaction
    this.continueButton.setInteractive(new Phaser.Geom.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight), Phaser.Geom.Rectangle.Contains);

    this.continueButton.on('pointerover', () => {
      buttonBg.clear();
      buttonBg.fillStyle(0xc89530, 1);
      buttonBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
      buttonBg.lineStyle(2, 0xe6b832, 1);
      buttonBg.strokeRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
    });

    this.continueButton.on('pointerout', () => {
      buttonBg.clear();
      buttonBg.fillStyle(0xe6a640, 1);
      buttonBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
      buttonBg.lineStyle(2, 0xd4a030, 0.8);
      buttonBg.strokeRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
    });

    this.continueButton.on('pointerdown', () => {
      this.scene.sound.play('click');
      // Button press effect
      buttonBg.scaleX = 0.95;
      buttonBg.scaleY = 0.95;
    });

    this.continueButton.on('pointerup', () => {
      buttonBg.scaleX = 1;
      buttonBg.scaleY = 1;
      this.onContinue?.();
    });

    this.add(this.continueButton);
  }

  show(data: BattleResultData, onContinue: () => void): void {
    this.onContinue = onContinue;

    // Update title based on outcome
    const titleColor = data.outcome === 'VICTORY' ? '#5cd6a8' : '#e05545';
    this.titleText.setText(data.outcome === 'VICTORY' ? 'VICTORY!' : 'DEFEAT');
    this.titleText.setColor(titleColor);

    // Update stats
    const healthPercent = Math.round((data.baseHealthRemaining / 100) * 100);
    const stats = [
      `━━━━━━━━━━━━━━━━━━━━`,
      `Enemies Killed: ${data.enemiesKilled}`,
      `Waves Completed: ${data.wavesCompleted}`,
      `Base Health: ${data.baseHealthRemaining} (${healthPercent}%)`,
      `Money Earned: +${data.moneyEarned} FLOP`,
      `━━━━━━━━━━━━━━━━━━━━`,
    ];
    this.statsText.setText(stats.join('\n'));

    // Show with animation
    this.setScale(0);
    this.setVisible(true);
    this.scene.tweens.add({
      targets: this,
      scaleX: 1,
      scaleY: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });
  }

  hide(): void {
    this.scene.tweens.add({
      targets: this,
      scaleX: 0,
      scaleY: 0,
      duration: 200,
      ease: 'Back.easeIn',
      onComplete: () => {
        this.setVisible(false);
      },
    });
  }
}

export default BattleResultDialog;
